/**
 * CDN Image URL Optimization Utilities
 *
 * Resolves local asset paths to CDN URLs with on-the-fly optimization
 * (WebP conversion, minification, resize). Falls back to original path
 * when no CDN mapping exists (e.g., user-uploaded items or unmapped assets).
 */

/** Picsart CDN optimization params: convert to WebP, minify, resize to 400px */
const CDN_OPTIMIZATION_PARAMS = '?type=webp&to=min&r=400';

/**
 * Check if a URL is a Picsart CDN URL
 */
export const isCdnUrl = (url: string): boolean => {
  return /cdn[\w-]*\.picsart\.(io|com)/.test(url);
};

/**
 * Resolve a thumbnail path to an optimized CDN URL.
 *
 * - If the path has a CDN mapping, returns the CDN URL with optimization params.
 * - If the URL is already a CDN URL (e.g., user-uploaded), returns it with optimization params.
 * - Otherwise, returns the original path unchanged (local fallback).
 *
 * @param path - Local asset path or CDN URL
 * @returns Optimized CDN URL or original path
 */
export const resolveImageUrl = (path: string): string => {
  if (!path) return path;

  // CDN URL — add optimization params
  if (isCdnUrl(path) && !path.includes('?')) {
    return `${path}${CDN_OPTIMIZATION_PARAMS}`;
  }

  // No mapping — return original (local path fallback)
  return path;
};

/**
 * Strip CDN optimization query params from a URL.
 * Used when passing URLs to the generation API, which needs the raw CDN URL.
 */
export const stripOptimizationParams = (url: string): string => {
  if (!isCdnUrl(url)) return url;
  try {
    const parsed = new URL(url);
    parsed.search = '';
    return parsed.toString();
  } catch {
    // If URL parsing fails, strip manually
    const qIndex = url.indexOf('?');
    return qIndex > -1 ? url.substring(0, qIndex) : url;
  }
};
